import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatDuration, mediaUrl } from "@/lib/format";
import { CategoryThumb } from "@/components/CategoryThumb";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Clock, Tag, Heart } from "lucide-react";

export default function SavedServices() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/me/likes/services").then((r) => setServices(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const unlike = async (e, sid) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/services/${sid}/like`);
      setServices((prev) => prev.filter((s) => s.id !== sid));
      toast.success("Removed from your saved services.");
    } catch (err) { toast.error("Couldn't update. Please try again."); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="saved-page">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">Saved services</h1>
      <p className="text-muted-foreground mt-2">The services you've hearted. Tap any card to book, or remove ones you no longer want.</p>

      {loading ? (
        <p className="text-muted-foreground mt-12">Loading…</p>
      ) : services.length === 0 ? (
        <div className="mt-12 text-center border border-dashed border-border rounded-sm p-12" data-testid="saved-empty">
          <Heart className="w-8 h-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-3 font-medium">No saved services yet</p>
          <p className="text-sm text-muted-foreground">Tap the heart on any service while browsing to save it here.</p>
          <Link to="/browse"><Button className="rounded-sm mt-4" data-testid="saved-browse-btn">Browse services</Button></Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {services.map((s) => (
            <Link key={s.id} to={`/service/${s.id}`} data-testid={`saved-card-${s.id}`}
              className="group bg-white border border-border rounded-sm overflow-hidden hard-shadow-hover transition-all hover:-translate-y-[2px]">
              <div className="h-44 overflow-hidden bg-muted relative">
                <CategoryThumb imageUrl={s.image_url} category={s.category} testid={`saved-image-${s.id}`}
                  imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <button onClick={(e) => unlike(e, s.id)} data-testid={`saved-unlike-${s.id}`}
                  className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors shadow-sm">
                  <Heart className="w-4 h-4 fill-primary text-primary" />
                </button>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Tag className="w-3 h-3" /> {s.category}</div>
                <h3 className="font-display text-xl font-medium mt-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.description || "—"}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <span className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="w-4 h-4" />{formatDuration(s.duration_minutes)}</span>
                  <span className="font-display text-lg font-semibold text-primary">${s.price}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                  {s.subscriber_logo && <img src={mediaUrl(s.subscriber_logo)} alt="" className="w-4 h-4 rounded-full object-cover" />}
                  by{" "}
                  <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/p/${s.subscriber_id}`); }}
                    className="hover:text-primary hover:underline cursor-pointer" data-testid={`saved-provider-${s.subscriber_id}`}>
                    {s.subscriber_name}
                  </span>
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
